import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HotelCard, { Hotel } from '@/components/HotelCard';
import HotelsMap from '@/components/HotelsMap';
import MapProvider from '@/components/MapProvider';
import { MagnifyingGlassIcon, ChevronDownIcon, XMarkIcon, Squares2X2Icon, MapIcon } from '@heroicons/react/24/outline';
import { themeClasses, preloadImages, Button } from '@member-portal/ui';
import { getCacheKey, getFromCache, setInCache } from '@/utils/cache';
import type { CollectionListSnapshot } from '@/types/collectionList';
import { useAuth } from '@/contexts/AuthContext';

/** Same pattern as a typical occupation/job semantic matcher: embedding index + query vector + cosine rank. */
const SEMANTIC_SEARCH_MIN_CHARS = 2;
/** Must stay ≤ /api/vendors Zod max (500). */
const SEMANTIC_FETCH_LIMIT = 500;

async function fetchSemanticRankings(
  query: string
): Promise<{ id: string; score: number }[] | null> {
  try {
    const params = new URLSearchParams({
      q: query.trim(),
      topK: String(SEMANTIC_FETCH_LIMIT),
      minScore: '0.22',
    });
    const res = await fetch(`/api/fhxd/collection/semantic-rank?${params}`);
    if (!res.ok) return null;
    const json = await res.json();
    if (!json.success || !Array.isArray(json.data?.rankings)) return null;
    return json.data.rankings.length > 0 ? json.data.rankings : null;
  } catch {
    return null;
  }
}

/**
 * Reorder by embedding similarity when we have a score, but never drop rows that already
 * matched the keyword SOQL filter — those IDs may be missing from the rank list or below minScore.
 */
function mergeSemanticRanking(
  vendors: Hotel[],
  rankings: { id: string; score: number }[]
): Hotel[] {
  const order = new Map(rankings.map((r, i) => [r.id, i]));
  const matched: Hotel[] = [];
  const rest: Hotel[] = [];
  for (const v of vendors) {
    if (order.has(v.id)) matched.push(v);
    else rest.push(v);
  }
  matched.sort((a, b) => (order.get(a.id)! - order.get(b.id)!));
  rest.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  return [...matched, ...rest];
}

const regionOptions = [
  { value: 'US & Canada', label: 'United States & Canada' },
  { value: 'Caribbean', label: 'Caribbean' },
  { value: 'Mexico Central & South America', label: 'Mexico, Central & South America' },
  { value: 'Europe', label: 'Europe' },
  { value: 'Africa Middle East', label: 'Africa & Middle East' },
  { value: 'Asia Pacific', label: 'Asia Pacific' },
];

const propertyTypeOptions = [
  { value: 'all', label: 'Participating Partners' },
  { value: 'FHXD Hotel', label: 'Hotels' },
  { value: 'FHXD Villa', label: 'Villas' },
  { value: 'FHXD Cruises', label: 'Specialty Cruise Lines' },
];

/** Default Collection record types when filter is "Participating Partners" */
const ALL_COLLECTION_RECORD_TYPES = 'FHXD Hotel,FHXD Villa,FHXD Cruises';

const bedroomOptions = [
  { value: '', label: 'Any Bedrooms' },
  { value: '1', label: '1 Bedroom' },
  { value: '2', label: '2 Bedrooms' },
  { value: '3', label: '3 Bedrooms' },
  { value: '4', label: '4 Bedrooms' },
  { value: '5', label: '5+ Bedrooms' },
];

interface CityOption {
  city: string;
  state?: string;
  country?: string;
  count: number;
}

interface CountryOption {
  country: string;
  count: number;
}

/** Map Salesforce country codes (e.g. US, GB) to English display names for the UI. API still uses codes. */
function getCountryDisplayName(code: string): string {
  const trimmed = code?.trim();
  if (!trimmed) return code;
  const upper = trimmed.toUpperCase();
  try {
    if (typeof Intl !== 'undefined' && 'DisplayNames' in Intl) {
      const names = new Intl.DisplayNames(['en'], { type: 'region' });
      const label = names.of(upper);
      if (label && label !== upper) return label;
    }
  } catch {
    // fall through
  }
  return trimmed;
}

function HotelsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [allHotels, setAllHotels] = useState<Hotel[]>([]); // For map view
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedPropertyType, setSelectedPropertyType] = useState('all');
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedBedrooms, setSelectedBedrooms] = useState('');
  const [exclusiveUseOnly, setExclusiveUseOnly] = useState(false);
  const [cities, setCities] = useState<CityOption[]>([]);
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

  // Dropdown states
  const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [propertyDropdownOpen, setPropertyDropdownOpen] = useState(false);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [bedroomDropdownOpen, setBedroomDropdownOpen] = useState(false);
  const regionDropdownRef = useRef<HTMLDivElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const propertyDropdownRef = useRef<HTMLDivElement>(null);
  const cityDropdownRef = useRef<HTMLDivElement>(null);
  const bedroomDropdownRef = useRef<HTMLDivElement>(null);
  const filterBarRef = useRef<HTMLElement>(null);
  /** Skip first run of "reset page on filter change" (mount). */
  const isFirstFilterChangeEffect = useRef(true);
  /** While true, skip list side-effects that would clobber a collection restore (page, city picks). */
  const suppressRestoreSideEffectsRef = useRef(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 12;

  // Scroll state for hiding navbar
  const [hideNavbar, setHideNavbar] = useState(false);

  // Handle scroll to hide/show navbar when filter bar reaches it
  useEffect(() => {
    const handleScroll = () => {
      if (!filterBarRef.current) return;

      const filterBarRect = filterBarRef.current.getBoundingClientRect();
      const navbarHeight = 96; // h-24 = 96px

      // Hide navbar when filter bar reaches the navbar
      if (filterBarRect.top <= navbarHeight) {
        setHideNavbar(true);
      } else {
        setHideNavbar(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(event.target as Node)) {
        setRegionDropdownOpen(false);
      }
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setCountryDropdownOpen(false);
      }
      if (propertyDropdownRef.current && !propertyDropdownRef.current.contains(event.target as Node)) {
        setPropertyDropdownOpen(false);
      }
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target as Node)) {
        setCityDropdownOpen(false);
      }
      if (bedroomDropdownRef.current && !bedroomDropdownRef.current.contains(event.target as Node)) {
        setBedroomDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch countries (only when region is selected)
  useEffect(() => {
    if (!selectedRegion) {
      setCountries([]);
      setSelectedCountry('');
      setCities([]);
      setSelectedCities([]);
      return;
    }

    const fetchCountries = async () => {
      try {
        const recordType = selectedPropertyType !== 'all' ? selectedPropertyType : ALL_COLLECTION_RECORD_TYPES;
        const params = new URLSearchParams();
        params.append('recordType', recordType);
        params.append('region', selectedRegion);
        const response = await fetch(`/api/vendors/countries?${params.toString()}`);
        if (response.ok) {
          const result = await response.json();
          setCountries(result.data || []);
        }
      } catch (e) {
        console.error('Failed to fetch countries:', e);
      }
    };
    fetchCountries();
  }, [selectedPropertyType, selectedRegion]);

  // Fetch cities (only when region is selected; optional country filter)
  useEffect(() => {
    if (!selectedRegion) {
      return;
    }

    const fetchCities = async () => {
      try {
        const recordType = selectedPropertyType !== 'all' ? selectedPropertyType : ALL_COLLECTION_RECORD_TYPES;
        const params = new URLSearchParams();
        params.append('recordType', recordType);
        params.append('region', selectedRegion);
        if (selectedCountry) {
          params.append('country', selectedCountry);
        }
        const response = await fetch(`/api/vendors/cities?${params.toString()}`);
        if (response.ok) {
          const result = await response.json();
          setCities(result.data || []);
        }
      } catch (e) {
        console.error('Failed to fetch cities:', e);
      }
    };
    fetchCities();
  }, [selectedPropertyType, selectedRegion, selectedCountry]);

  // Changing country invalidates city selection (city list / API scope changes)
  useEffect(() => {
    if (suppressRestoreSideEffectsRef.current) return;
    setSelectedCities([]);
  }, [selectedCountry]);

  // Debounce search — only reset pagination when the query actually changes (not on restore sync)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery((dq) => {
        if (dq !== searchQuery && !suppressRestoreSideEffectsRef.current) {
          setPage(1);
        }
        return searchQuery;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when filters change (skip mount + skip while restoring from property detail)
  useEffect(() => {
    if (isFirstFilterChangeEffect.current) {
      isFirstFilterChangeEffect.current = false;
      return;
    }
    if (suppressRestoreSideEffectsRef.current) return;
    setPage(1);
  }, [selectedRegion, selectedCountry, selectedPropertyType, selectedCities, selectedBedrooms, exclusiveUseOnly]);

  // Clear bedrooms filter when switching away from villas, clear exclusive use when switching away from hotels
  useEffect(() => {
    if (selectedPropertyType !== 'FHXD Villa') {
      setSelectedBedrooms('');
    }
    if (selectedPropertyType !== 'FHXD Hotel') {
      setExclusiveUseOnly(false);
    }
  }, [selectedPropertyType]);

  // Build cache key from current filters
  const getCacheParams = useCallback(() => ({
    recordType: selectedPropertyType !== 'all' ? selectedPropertyType : ALL_COLLECTION_RECORD_TYPES,
    page: page.toString(),
    search: debouncedQuery,
    region: selectedRegion,
    country: selectedCountry,
    city: selectedCities.join(','),
    bedrooms: selectedBedrooms,
    exclusiveUse: exclusiveUseOnly.toString(),
  }), [selectedPropertyType, page, debouncedQuery, selectedRegion, selectedCountry, selectedCities, selectedBedrooms, exclusiveUseOnly]);

  const collectionListSnapshot = useMemo(
    (): CollectionListSnapshot => ({
      search: debouncedQuery,
      page,
      viewMode,
      region: selectedRegion,
      country: selectedCountry,
      propertyType: selectedPropertyType,
      cities: [...selectedCities],
      bedrooms: selectedBedrooms,
      exclusiveUse: exclusiveUseOnly,
    }),
    [
      debouncedQuery,
      page,
      viewMode,
      selectedRegion,
      selectedCountry,
      selectedPropertyType,
      selectedCities,
      selectedBedrooms,
      exclusiveUseOnly,
    ]
  );

  // Restore list state when returning from property detail ("Back to Collection")
  useEffect(() => {
    const restore = (location.state as { restore?: CollectionListSnapshot } | null)?.restore;
    if (!restore) return;

    suppressRestoreSideEffectsRef.current = true;
    setSearchQuery(restore.search);
    setDebouncedQuery(restore.search);
    setPage(Math.max(1, restore.page || 1));
    setViewMode(restore.viewMode === 'map' ? 'map' : 'list');
    setSelectedRegion(restore.region ?? '');
    setSelectedCountry(restore.country ?? '');
    setSelectedPropertyType(restore.propertyType ?? 'all');
    setSelectedCities(Array.isArray(restore.cities) ? [...restore.cities] : []);
    setSelectedBedrooms(restore.bedrooms ?? '');
    setExclusiveUseOnly(Boolean(restore.exclusiveUse));

    navigate('/collection', { replace: true, state: {} });
    setTimeout(() => {
      suppressRestoreSideEffectsRef.current = false;
    }, 0);
  }, [location.state, navigate]);

  // Load hotels with caching (semantic search bypasses cache; uses embedding index when available)
  useEffect(() => {
    const loadHotels = async () => {
      const eligibleSemantic = debouncedQuery.trim().length >= SEMANTIC_SEARCH_MIN_CHARS;

      let listCache: { data: { vendors: Hotel[]; totalCount: number }; isStale: boolean } | null = null;
      let cacheKey = '';

      if (!eligibleSemantic) {
        cacheKey = getCacheKey('hotels', getCacheParams());
        listCache = getFromCache<{ vendors: Hotel[]; totalCount: number }>(cacheKey);

        if (listCache) {
          setHotels(listCache.data.vendors);
          setTotalCount(listCache.data.totalCount);
          setLoading(false);

          if (!listCache.isStale) {
            return;
          }
        } else {
          setLoading(true);
        }
      } else {
        setLoading(true);
      }

      try {
        setError(null);

        let rankings: { id: string; score: number }[] | null = null;
        if (eligibleSemantic) {
          rankings = await fetchSemanticRankings(debouncedQuery);
        }
        const useSemanticOrdering = rankings != null && rankings.length > 0;
        const hasQuery = debouncedQuery.trim().length > 0;

        /**
         * Semantic mode used to omit `search` and only take the first 500 rows globally (Favorite + name),
         * then intersect with embedding ranks — real matches (e.g. all New York hotels) were often missing.
         * We always apply the same keyword filter as the list API when the user typed a query, then reorder
         * by semantic scores. If that returns zero rows (abstract query), fall back to the broad 500 + semantic.
         */
        const buildListParams = (includeKeywordSearch: boolean): URLSearchParams => {
          const p = new URLSearchParams();
          if (selectedPropertyType !== 'all') {
            p.append('recordType', selectedPropertyType);
          } else {
            p.append('recordType', ALL_COLLECTION_RECORD_TYPES);
          }
          if (useSemanticOrdering) {
            p.append('limit', String(SEMANTIC_FETCH_LIMIT));
            p.append('offset', '0');
          } else {
            p.append('limit', String(limit));
            p.append('offset', String((page - 1) * limit));
          }
          if (includeKeywordSearch && hasQuery) {
            p.append('search', debouncedQuery.trim());
          }
          if (selectedRegion) {
            p.append('region', selectedRegion);
          }
          if (selectedCountry) {
            p.append('country', selectedCountry);
          }
          if (selectedCities.length > 0) {
            p.append('city', selectedCities.join(','));
          }
          if (selectedBedrooms) {
            if (selectedBedrooms === '5') {
              p.append('minBedrooms', '5');
            } else {
              p.append('minBedrooms', selectedBedrooms);
              p.append('maxBedrooms', selectedBedrooms);
            }
          }
          if (exclusiveUseOnly) {
            p.append('exclusiveUse', 'true');
          }
          return p;
        };

        let params = buildListParams(hasQuery);
        let response = await fetch(`/api/vendors?${params.toString()}`);
        if (!response.ok) {
          throw new Error('Failed to fetch properties');
        }

        let result = await response.json();
        let data = result.data || result;
        let vendorsList: Hotel[] = data.vendors || [];
        let total = data.totalCount || 0;

        if (
          useSemanticOrdering &&
          rankings &&
          hasQuery &&
          vendorsList.length === 0 &&
          !data.locationStrictSearch
        ) {
          const semanticIds = rankings.slice(0, 100).map((r) => r.id).join(',');
          params = buildListParams(false);
          if (semanticIds) {
            params.append('vendorIds', semanticIds);
          }
          response = await fetch(`/api/vendors?${params.toString()}`);
          if (!response.ok) {
            throw new Error('Failed to fetch properties');
          }
          result = await response.json();
          data = result.data || result;
          vendorsList = data.vendors || [];
          total = data.totalCount || 0;
        }

        if (useSemanticOrdering && rankings) {
          vendorsList = mergeSemanticRanking(vendorsList, rankings);
          vendorsList = vendorsList.slice((page - 1) * limit, page * limit);
        }

        const freshData = { vendors: vendorsList, totalCount: total };

        if (!eligibleSemantic && cacheKey) {
          setInCache(cacheKey, freshData);
        }

        setHotels(freshData.vendors);
        setTotalCount(freshData.totalCount);
      } catch (e) {
        if (!listCache) {
          setError(e instanceof Error ? e.message : 'Failed to load properties');
        }
      } finally {
        setLoading(false);
      }
    };

    loadHotels();
  }, [page, debouncedQuery, selectedRegion, selectedCountry, selectedPropertyType, selectedCities, selectedBedrooms, exclusiveUseOnly, getCacheParams]);

  const totalPages = Math.ceil(totalCount / limit);

  // Preload hotel images in background when hotels change
  useEffect(() => {
    const urls = hotels.map(h => h.imageUrl).filter((url): url is string => !!url);
    if (urls.length > 0) {
      preloadImages(urls);
    }
  }, [hotels]);

  // Load all hotels for map view (no pagination; same semantic ordering as list when index exists)
  useEffect(() => {
    const loadAllHotels = async () => {
      try {
        const eligibleSemantic = debouncedQuery.trim().length >= SEMANTIC_SEARCH_MIN_CHARS;
        let rankings: { id: string; score: number }[] | null = null;
        if (eligibleSemantic) {
          rankings = await fetchSemanticRankings(debouncedQuery);
        }
        const useSemanticOrdering = rankings != null && rankings.length > 0;
        const hasQuery = debouncedQuery.trim().length > 0;

        const buildMapParams = (includeKeywordSearch: boolean): URLSearchParams => {
          const p = new URLSearchParams();
          if (selectedPropertyType !== 'all') {
            p.append('recordType', selectedPropertyType);
          } else {
            p.append('recordType', ALL_COLLECTION_RECORD_TYPES);
          }
          p.append('limit', useSemanticOrdering ? String(SEMANTIC_FETCH_LIMIT) : '500');
          p.append('offset', '0');
          if (includeKeywordSearch && hasQuery) {
            p.append('search', debouncedQuery.trim());
          }
          if (selectedRegion) {
            p.append('region', selectedRegion);
          }
          if (selectedCountry) {
            p.append('country', selectedCountry);
          }
          if (selectedCities.length > 0) {
            p.append('city', selectedCities.join(','));
          }
          if (selectedBedrooms) {
            if (selectedBedrooms === '5') {
              p.append('minBedrooms', '5');
            } else {
              p.append('minBedrooms', selectedBedrooms);
              p.append('maxBedrooms', selectedBedrooms);
            }
          }
          if (exclusiveUseOnly) {
            p.append('exclusiveUse', 'true');
          }
          return p;
        };

        let params = buildMapParams(hasQuery);
        let response = await fetch(`/api/vendors?${params.toString()}`);
        if (!response.ok) {
          throw new Error('Failed to fetch properties');
        }

        let result = await response.json();
        let data = result.data || result;
        let vendorsList: Hotel[] = data.vendors || [];

        if (
          useSemanticOrdering &&
          rankings &&
          hasQuery &&
          vendorsList.length === 0 &&
          !data.locationStrictSearch
        ) {
          const semanticIds = rankings.slice(0, 100).map((r) => r.id).join(',');
          params = buildMapParams(false);
          if (semanticIds) {
            params.append('vendorIds', semanticIds);
          }
          response = await fetch(`/api/vendors?${params.toString()}`);
          if (!response.ok) {
            throw new Error('Failed to fetch properties');
          }
          result = await response.json();
          data = result.data || result;
          vendorsList = data.vendors || [];
        }

        if (useSemanticOrdering && rankings) {
          vendorsList = mergeSemanticRanking(vendorsList, rankings);
        }
        setAllHotels(vendorsList);
      } catch (e) {
        console.error('Failed to load all hotels for map:', e);
      }
    };

    loadAllHotels();
  }, [debouncedQuery, selectedRegion, selectedCountry, selectedPropertyType, selectedCities, selectedBedrooms, exclusiveUseOnly]);

  const toggleCity = (city: string) => {
    setSelectedCities(prev =>
      prev.includes(city)
        ? prev.filter(c => c !== city)
        : [...prev, city]
    );
  };

  const clearAllFilters = () => {
    setSelectedRegion('');
    setSelectedCountry('');
    setSelectedPropertyType('all');
    setSelectedCities([]);
    setSelectedBedrooms('');
    setExclusiveUseOnly(false);
    setSearchQuery('');
  };

  const activeFilterCount = (selectedRegion ? 1 : 0) +
    (selectedCountry ? 1 : 0) +
    (selectedPropertyType !== 'all' ? 1 : 0) +
    selectedCities.length +
    (selectedBedrooms ? 1 : 0) +
    (exclusiveUseOnly ? 1 : 0);

  return (
    <div className="min-h-screen bg-theme-bg">
      {/* Navbar Spacer - adjusts based on navbar visibility */}
      <div className={`transition-all duration-300 fhxd-chrome-spacer ${hideNavbar ? '!h-0' : ''}`} />

      {/* Hero Section - Compact */}
      <section className="relative py-16 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/images/images/minhojiddin-fazliddinov-SQi6QAJYjvE-unsplash.jpg)' }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className={`relative z-10 ${themeClasses.container} text-center`}>
          <h1 className="font-title font-normal text-2xl md:text-4xl lg:text-5xl tracking-wider normal-case text-white mb-3">
            FHxD COLLECTION
          </h1>
          <p className="font-body font-normal leading-normal tracking-form text-white/70 max-w-xl mx-auto">
            Explore our curated collection of hotels, resorts, villas, private residences, specialty cruise lines and providers of luxury goods and services.
          </p>
        </div>
      </section>

      {/* Compact Filter Bar - Sticky */}
      <section
        ref={filterBarRef}
        className={`sticky z-40 bg-theme-bg border-b border-theme-border transition-all duration-300 ${
          hideNavbar ? 'top-0' : 'top-24'
        }`}
      >
        <div className={`${themeClasses.container} py-3`}>
          <div className="flex flex-wrap items-start gap-3">
            {/* Search Input */}
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-theme-text-muted" />
                <input
                  type="text"
                  placeholder="Search Collection by Name"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`${themeClasses.form.input} pl-12 font-normal placeholder:font-normal`}
                />
              </div>
              <p className={`${themeClasses.text.muted} text-xs mt-2`}>
                Search is optimized when using Participating Partner Category, Region, Country, and City filters.
              </p>
            </div>

            {/* Property Type Dropdown */}
            <div className="relative" ref={propertyDropdownRef}>
              <button
                onClick={() => {
                  setPropertyDropdownOpen(!propertyDropdownOpen);
                  setRegionDropdownOpen(false);
                  setCountryDropdownOpen(false);
                }}
                className={`${themeClasses.text.body} flex items-center gap-2 px-4 py-3 border transition-colors ${
                  selectedPropertyType !== 'all'
                    ? 'border-theme-text text-theme-text bg-theme-bg-secondary'
                    : 'border-theme-border text-theme-text-muted hover:text-theme-text hover:border-theme-text'
                }`}
              >
                <span>{propertyTypeOptions.find(o => o.value === selectedPropertyType)?.label}</span>
                <ChevronDownIcon className={`h-5 w-5 transition-transform ${propertyDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {propertyDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 min-w-full bg-theme-bg border border-theme-border shadow-lg z-50">
                  {propertyTypeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSelectedPropertyType(option.value);
                        setPropertyDropdownOpen(false);
                      }}
                      className={`font-body font-normal tracking-form w-full text-left px-4 py-3 transition-colors whitespace-nowrap ${
                        selectedPropertyType === option.value
                          ? 'bg-theme-accent text-theme-accent-text'
                          : 'text-theme-text-muted hover:text-theme-text hover:bg-theme-bg-secondary'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Region Dropdown */}
            <div className="relative" ref={regionDropdownRef}>
              <button
                onClick={() => {
                  setRegionDropdownOpen(!regionDropdownOpen);
                  setPropertyDropdownOpen(false);
                  setCountryDropdownOpen(false);
                }}
                className={`${themeClasses.text.body} flex items-center gap-2 px-4 py-3 border transition-colors ${
                  selectedRegion
                    ? 'border-theme-text text-theme-text bg-theme-bg-secondary'
                    : 'border-theme-border text-theme-text-muted hover:text-theme-text hover:border-theme-text'
                }`}
              >
                <span>
                  {selectedRegion
                    ? regionOptions.find(r => r.value === selectedRegion)?.label
                    : 'All Regions'}
                </span>
                <ChevronDownIcon className={`h-5 w-5 transition-transform ${regionDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {regionDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 min-w-[280px] bg-theme-bg border border-theme-border shadow-lg z-50">
                  <button
                    onClick={() => {
                      setSelectedRegion('');
                      setSelectedCountry('');
                      setRegionDropdownOpen(false);
                    }}
                    className={`font-body font-normal tracking-form w-full text-left px-4 py-3 transition-colors whitespace-nowrap ${
                      !selectedRegion
                        ? 'bg-theme-accent text-theme-accent-text'
                        : 'text-theme-text-muted hover:text-theme-text hover:bg-theme-bg-secondary'
                    }`}
                  >
                    All Regions
                  </button>
                  {regionOptions.map((region) => (
                    <button
                      key={region.value}
                      onClick={() => {
                        setSelectedRegion(region.value);
                        setSelectedCountry('');
                        setRegionDropdownOpen(false);
                      }}
                      className={`font-body font-normal tracking-form w-full text-left px-4 py-3 transition-colors whitespace-nowrap ${
                        selectedRegion === region.value
                          ? 'bg-theme-accent text-theme-accent-text'
                          : 'text-theme-text-muted hover:text-theme-text hover:bg-theme-bg-secondary'
                      }`}
                    >
                      {region.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Country Dropdown - only after a region is selected */}
            {selectedRegion && countries.length > 0 && (
              <div className="relative" ref={countryDropdownRef}>
                <button
                  onClick={() => {
                    setCountryDropdownOpen(!countryDropdownOpen);
                    setRegionDropdownOpen(false);
                    setPropertyDropdownOpen(false);
                    setCityDropdownOpen(false);
                    setBedroomDropdownOpen(false);
                  }}
                  className={`${themeClasses.text.body} flex items-center gap-2 px-4 py-3 border transition-colors ${
                    selectedCountry
                      ? 'border-theme-text text-theme-text bg-theme-bg-secondary'
                      : 'border-theme-border text-theme-text-muted hover:text-theme-text hover:border-theme-text'
                  }`}
                >
                  <span>
                    {selectedCountry ? getCountryDisplayName(selectedCountry) : 'All Countries'}
                  </span>
                  <ChevronDownIcon className={`h-5 w-5 transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {countryDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 min-w-[220px] max-h-[300px] overflow-y-auto bg-theme-bg border border-theme-border shadow-lg z-50">
                    <button
                      onClick={() => {
                        setSelectedCountry('');
                        setCountryDropdownOpen(false);
                      }}
                      className={`font-body font-normal tracking-form w-full text-left px-4 py-3 transition-colors whitespace-nowrap ${
                        !selectedCountry
                          ? 'bg-theme-accent text-theme-accent-text'
                          : 'text-theme-text-muted hover:text-theme-text hover:bg-theme-bg-secondary'
                      }`}
                    >
                      All Countries
                    </button>
                    {countries.map((c) => (
                      <button
                        key={c.country}
                        onClick={() => {
                          setSelectedCountry(c.country);
                          setCountryDropdownOpen(false);
                        }}
                        className={`font-body font-normal tracking-form w-full text-left px-4 py-3 transition-colors whitespace-nowrap ${
                          selectedCountry === c.country
                            ? 'bg-theme-accent text-theme-accent-text'
                            : 'text-theme-text-muted hover:text-theme-text hover:bg-theme-bg-secondary'
                        }`}
                      >
                        {getCountryDisplayName(c.country)} ({c.count})
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* City Multi-Select Dropdown - only show if a region is selected */}
            {selectedRegion && cities.length > 0 && (
              <div className="relative" ref={cityDropdownRef}>
                <button
                  onClick={() => {
                    setCityDropdownOpen(!cityDropdownOpen);
                    setRegionDropdownOpen(false);
                    setCountryDropdownOpen(false);
                    setPropertyDropdownOpen(false);
                    setBedroomDropdownOpen(false);
                  }}
                  className={`${themeClasses.text.body} flex items-center gap-2 px-4 py-3 border transition-colors ${
                    selectedCities.length > 0
                      ? 'border-theme-text text-theme-text bg-theme-bg-secondary'
                      : 'border-theme-border text-theme-text-muted hover:text-theme-text hover:border-theme-text'
                  }`}
                >
                  <span>
                    {selectedCities.length === 0
                      ? 'All Cities'
                      : selectedCities.length === 1
                        ? selectedCities[0]
                        : `${selectedCities.length} Cities`}
                  </span>
                  <ChevronDownIcon className={`h-5 w-5 transition-transform ${cityDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {cityDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 min-w-[250px] max-h-[300px] overflow-y-auto bg-theme-bg border border-theme-border shadow-lg z-50">
                    <div className="p-3 border-b border-theme-border">
                      <button
                        onClick={() => setSelectedCities([])}
                        className={`${themeClasses.text.caption} hover:text-theme-text transition-colors`}
                      >
                        Clear selection
                      </button>
                    </div>
                    {cities.map((city) => (
                      <label
                        key={city.city}
                        className={`${themeClasses.text.body} flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-theme-bg-secondary transition-colors`}
                      >
                        <div className={`w-5 h-5 border flex items-center justify-center transition-colors ${
                          selectedCities.includes(city.city)
                            ? 'bg-theme-accent border-theme-accent'
                            : 'bg-transparent border-theme-border'
                        }`}>
                          {selectedCities.includes(city.city) && (
                            <svg className="w-3 h-3 text-theme-accent-text" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={selectedCities.includes(city.city)}
                          onChange={() => toggleCity(city.city)}
                          className="sr-only"
                        />
                        <span className={selectedCities.includes(city.city) ? 'text-theme-text' : 'text-theme-text-muted'}>
                          {city.city}{city.country ? `, ${getCountryDisplayName(city.country)}` : ''} ({city.count})
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Bedrooms Dropdown - only show when villas are selected */}
            {selectedPropertyType === 'FHXD Villa' && (
              <div className="relative" ref={bedroomDropdownRef}>
                <button
                  onClick={() => {
                    setBedroomDropdownOpen(!bedroomDropdownOpen);
                    setRegionDropdownOpen(false);
                    setCountryDropdownOpen(false);
                    setPropertyDropdownOpen(false);
                    setCityDropdownOpen(false);
                  }}
                  className={`${themeClasses.text.body} flex items-center gap-2 px-4 py-3 border transition-colors ${
                    selectedBedrooms
                      ? 'border-theme-text text-theme-text bg-theme-bg-secondary'
                      : 'border-theme-border text-theme-text-muted hover:text-theme-text hover:border-theme-text'
                  }`}
                >
                  <span>{bedroomOptions.find(o => o.value === selectedBedrooms)?.label || 'Any Bedrooms'}</span>
                  <ChevronDownIcon className={`h-5 w-5 transition-transform ${bedroomDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {bedroomDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 min-w-full bg-theme-bg border border-theme-border shadow-lg z-50">
                    {bedroomOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedBedrooms(option.value);
                          setBedroomDropdownOpen(false);
                        }}
                        className={`font-body font-normal tracking-form w-full text-left px-4 py-3 transition-colors whitespace-nowrap ${
                          selectedBedrooms === option.value
                            ? 'bg-theme-accent text-theme-accent-text'
                            : 'text-theme-text-muted hover:text-theme-text hover:bg-theme-bg-secondary'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Exclusive Use Toggle - only show when hotels are selected */}
            {selectedPropertyType === 'FHXD Hotel' && (
              <button
                onClick={() => setExclusiveUseOnly(!exclusiveUseOnly)}
                className={`${themeClasses.text.body} flex items-center gap-2 px-4 py-3 border transition-colors ${
                  exclusiveUseOnly
                    ? 'border-theme-text text-theme-text bg-theme-bg-secondary'
                    : 'border-theme-border text-theme-text-muted hover:text-theme-text hover:border-theme-text'
                }`}
              >
                <div className={`w-5 h-5 border flex items-center justify-center transition-colors ${
                  exclusiveUseOnly
                    ? 'bg-theme-text border-theme-text'
                    : 'bg-transparent border-theme-border'
                }`}>
                  {exclusiveUseOnly && (
                    <svg className="w-3 h-3 text-theme-bg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span>Exclusive Use</span>
              </button>
            )}

            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                className={`${themeClasses.text.body} flex items-center gap-2 px-4 py-3 text-theme-text-muted hover:text-theme-text transition-colors`}
              >
                <XMarkIcon className="h-5 w-5" />
                <span>Clear</span>
              </button>
            )}

            {/* Results Count - only show when filtering */}
            {(activeFilterCount > 0 || debouncedQuery) && (
              <div className={`${themeClasses.text.muted} ml-auto`}>
                {loading ? 'Loading...' : `${totalCount} Participating Partners`}
              </div>
            )}

            {/* View Toggle */}
            <div className="flex items-center border border-theme-border">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-3 transition-colors ${
                  viewMode === 'list'
                    ? 'bg-theme-accent text-theme-accent-text'
                    : 'text-theme-text-muted hover:text-theme-text'
                }`}
                aria-label="List view"
              >
                <Squares2X2Icon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`px-3 py-3 transition-colors ${
                  viewMode === 'map'
                    ? 'bg-theme-accent text-theme-accent-text'
                    : 'text-theme-text-muted hover:text-theme-text'
                }`}
                aria-label="Map view"
              >
                <MapIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Active Filter Pills */}
          {(selectedRegion || selectedCountry || selectedPropertyType !== 'all' || selectedCities.length > 0 || selectedBedrooms) && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-theme-border">
              {selectedPropertyType !== 'all' && (
                <button
                  onClick={() => setSelectedPropertyType('all')}
                  className={`${themeClasses.text.mutedSmall} inline-flex items-center gap-2 px-3 py-1.5 bg-theme-bg-secondary border border-theme-border text-theme-text hover:border-theme-text transition-colors`}
                >
                  <span>{propertyTypeOptions.find(o => o.value === selectedPropertyType)?.label.replace(' Only', '')}</span>
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
              {selectedRegion && (
                <button
                  onClick={() => setSelectedRegion('')}
                  className={`${themeClasses.text.mutedSmall} inline-flex items-center gap-2 px-3 py-1.5 bg-theme-bg-secondary border border-theme-border text-theme-text hover:border-theme-text transition-colors`}
                >
                  <span>{regionOptions.find(r => r.value === selectedRegion)?.label}</span>
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
              {selectedCountry && (
                <button
                  onClick={() => setSelectedCountry('')}
                  className={`${themeClasses.text.mutedSmall} inline-flex items-center gap-2 px-3 py-1.5 bg-theme-bg-secondary border border-theme-border text-theme-text hover:border-theme-text transition-colors`}
                >
                  <span>{getCountryDisplayName(selectedCountry)}</span>
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
              {selectedCities.map(city => (
                <button
                  key={city}
                  onClick={() => toggleCity(city)}
                  className={`${themeClasses.text.mutedSmall} inline-flex items-center gap-2 px-3 py-1.5 bg-theme-bg-secondary border border-theme-border text-theme-text hover:border-theme-text transition-colors`}
                >
                  <span>{city}</span>
                  <XMarkIcon className="h-4 w-4" />
                </button>
              ))}
              {selectedBedrooms && (
                <button
                  onClick={() => setSelectedBedrooms('')}
                  className={`${themeClasses.text.mutedSmall} inline-flex items-center gap-2 px-3 py-1.5 bg-theme-bg-secondary border border-theme-border text-theme-text hover:border-theme-text transition-colors`}
                >
                  <span>{bedroomOptions.find(o => o.value === selectedBedrooms)?.label}</span>
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
              {exclusiveUseOnly && (
                <button
                  onClick={() => setExclusiveUseOnly(false)}
                  className={`${themeClasses.text.mutedSmall} inline-flex items-center gap-2 px-3 py-1.5 bg-theme-bg-secondary border border-theme-border text-theme-text hover:border-theme-text transition-colors`}
                >
                  <span>Exclusive Use</span>
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Content Area - List or Map */}
      {viewMode === 'list' ? (
        <section className="py-12 bg-theme-bg">
          <div className={themeClasses.container}>
            {loading ? (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-theme-text mx-auto"></div>
                <p className={`${themeClasses.text.muted} mt-4`}>Loading properties...</p>
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <p className="text-red-600 mb-4">{error}</p>
                <Button
                  variant="secondary"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </Button>
              </div>
            ) : hotels.length > 0 ? (
              <>
                {/* Grid */}
                <div className={themeClasses.grid.cols3}>
                  {hotels.map((hotel) => (
                    <HotelCard
                      key={hotel.id}
                      hotel={hotel}
                      collectionListSnapshot={collectionListSnapshot}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-12 flex items-center justify-center gap-4">
                    <Button
                      variant="outline"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <span className={themeClasses.text.muted}>
                      {page} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16">
                <p className={themeClasses.text.muted}>
                  {debouncedQuery || selectedRegion || selectedCities.length > 0
                    ? 'No partners found matching your filters.'
                    : 'No partners available at the moment.'}
                </p>
                {activeFilterCount > 0 && (
                  <Button
                    variant="secondary"
                    onClick={clearAllFilters}
                    className="mt-4"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            )}
          </div>
        </section>
      ) : (
        <section className="py-12 bg-theme-bg">
          <div className={themeClasses.container}>
            <div style={{ height: '600px' }}>
              <MapProvider>
                <HotelsMap
                  hotels={allHotels}
                  className="w-full h-full"
                  collectionListSnapshot={collectionListSnapshot}
                />
              </MapProvider>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section — hide when logged in */}
      {!isAuthenticated && (
        <section className="py-12 bg-theme-bg-secondary border-t border-theme-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className={`${themeClasses.title.h4} mb-3`}>
              READY TO GET STARTED?
            </h2>
            <p className={`${themeClasses.text.muted} mb-6`}>
              Discover the FHxD world-class travel partner collection and earn more, faster.
            </p>
            <Button variant="primary" onClick={() => navigate('/signup')}>
              Advisor Sign Up Now
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}

export default HotelsPage;
